import { analytics } from "@/content";
import { Rich, toneClass } from "@/lib/rich";

export default function AnalyticsHero() {
  const { hero } = analytics;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center bg-white dark:bg-[#0A0A0A] overflow-hidden pt-20"
    >
      {/* Мягкие цветовые пятна */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[5%] w-[550px] h-[550px] rounded-full bg-blue-100/40 dark:bg-blue-500/[0.12] blur-[140px]" />
        <div className="absolute bottom-[10%] left-[-5%] w-[450px] h-[450px] rounded-full bg-slate-100/60 dark:bg-white/[0.04] blur-[120px]" />
        <div className="absolute top-[35%] left-[10%] w-[400px] h-[400px] rounded-full bg-emerald-100/30 dark:bg-emerald-500/[0.08] blur-[130px]" />
        <div className="absolute bottom-[15%] right-[5%] w-[450px] h-[450px] rounded-full bg-blue-100/30 dark:bg-blue-500/[0.10] blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-24 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
          {/* Текстовая часть */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/[0.12] border border-blue-100 dark:border-blue-400/25 text-blue-700 dark:text-blue-400 text-xs font-semibold mb-7">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block animate-pulse"></span>
              {hero.badge}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-extrabold text-slate-900 dark:text-white leading-[1.08] mb-6 tracking-tight">
              <Rich text={hero.title} accentClassName="text-blue-600 dark:text-blue-400" />
            </h1>

            <p className="text-slate-600 dark:text-white/65 text-lg md:text-xl leading-relaxed mb-4 max-w-xl mx-auto lg:mx-0">
              {hero.lead}
            </p>
            <p className="text-slate-500 dark:text-white/60 text-base leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0">
              {hero.text}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href={hero.primaryCta.href}
                className="px-8 py-4 rounded-xl bg-blue-600 text-white font-bold text-base hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap shadow-sm shadow-blue-200"
              >
                {hero.primaryCta.label}
              </a>
              <a
                href={hero.secondaryCta.href}
                className="px-8 py-4 rounded-xl border border-slate-300 dark:border-white/[0.14] text-slate-700 dark:text-white/80 font-semibold text-base hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {hero.secondaryCta.label}
              </a>
            </div>

            <div className="mt-14 flex flex-wrap gap-8 justify-center lg:justify-start">
              {hero.stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-2xl font-extrabold text-slate-900 dark:text-white">
                    {stat.value}
                  </div>
                  <div className="text-slate-500 dark:text-white/60 text-xs mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Превью дашборда */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[480px]">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/60 to-slate-100/40 blur-[60px] rounded-3xl scale-95" />
              <div className="relative bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] rounded-3xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-slate-500 dark:text-white/60 text-xs font-medium">
                    {hero.dashboard.caption}
                  </span>
                </div>

                {/* Метрики */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {hero.dashboard.metrics.map((metric) => (
                    <div
                      key={metric.label}
                      className="bg-slate-50 dark:bg-white/[0.05] rounded-xl p-3 text-center border border-slate-100 dark:border-white/[0.08]"
                    >
                      <div className="text-slate-500 dark:text-white/60 text-[10px] mb-1">
                        {metric.label}
                      </div>
                      <div className="text-slate-900 dark:text-white font-bold text-sm">
                        {metric.value}
                      </div>
                      <div
                        className={`text-[10px] font-semibold ${
                          metric.positive ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {metric.change}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Воронка */}
                <div className="mb-5">
                  <div className="text-slate-500 dark:text-white/60 text-xs mb-3">
                    {hero.dashboard.funnelTitle}
                  </div>
                  <div className="space-y-2">
                    {hero.dashboard.funnel.map((item) => (
                      <div key={item.stage} className="flex items-center gap-3">
                        <span className="text-slate-500 dark:text-white/60 text-xs w-20 text-right flex-shrink-0">
                          {item.stage}
                        </span>
                        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${toneClass(
                              item.tone,
                              "bg-blue-600"
                            )}`}
                            style={{ width: `${item.percent}%` }}
                          />
                        </div>
                        <span className="text-slate-900 dark:text-white font-bold text-xs w-8">
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI-инсайт */}
                <div className="bg-blue-50 dark:bg-blue-500/[0.12] border border-blue-100 dark:border-blue-400/25 rounded-xl p-3 flex items-start gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-600 flex-shrink-0 mt-0.5">
                    <i className="ri-robot-line text-white text-xs"></i>
                  </div>
                  <div>
                    <div className="text-blue-800 dark:text-blue-300 text-xs font-semibold mb-0.5">
                      {hero.dashboard.insight.label}
                    </div>
                    <div className="text-blue-700 dark:text-blue-400 text-xs leading-relaxed">
                      {hero.dashboard.insight.text}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
